import { Button, ButtonGroup, Grid, Paper, styled } from "@mui/material";
import { Pagination, Skeleton } from "@mui/material";
import React, { FC, useState } from "react";
import { images } from "../../constants";
import { Press } from "../../interfaces";
import { formatDateMY } from "../../utils/date";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DoDisturbRoundedIcon from "@mui/icons-material/DoDisturbRounded";
import Image from "next/image";
import { safeUrl } from "../../utils/security";

const InTheNews: FC<Props> = ({
  strings,
  newsList,
  loading,
  compact = false,
}) => {
  const [page, setPage] = useState(1);
  const MAX = compact ? 3 : 9;

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // An unsafe or empty link opens nothing, rather than a new tab on "#".
  const handleButtonClick = (url: string) => {
    const href = safeUrl(url);
    if (href === "#") return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const startIndex = (page - 1) * MAX;
  const endIndex = startIndex + MAX;
  const displayedNews = (newsList || []).slice(startIndex, endIndex);

  return (
    <section
      className="sectionPadding centerContent"
      style={compact ? { padding: "2rem 0 5rem" } : {}}
    >
      <h2>{strings.in_the_news}</h2>
      <p>{strings.in_the_news_description}</p>
      <Grid container spacing={4}>
        {!loading ? (
          newsList.length ? (
            displayedNews.map((news) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={news.articleTitle + Math.random()}
              >
                <StyledPaper elevation={0}>
                  <div className="cardContent">
                    <div className="logoHolder">
                      <Image
                        src={news.publicationLogo || images.dummy}
                        alt={news.publicationName}
                        width={100}
                        height={30}
                        style={{ height: "30px", width: "auto" }}
                      />
                    </div>
                    <p>
                      <time
                        className="cardDate"
                        dateTime={new Date(news.publicationDateTs).toISOString()}
                      >
                        {formatDateMY(news.publicationDateTs)}
                      </time>
                    </p>
                    <h3>{news.articleTitle}</h3>
                  </div>
                  <ButtonGroup
                    variant="text"
                    size="small"
                    aria-label="Small button group"
                    fullWidth
                    sx={{ borderTop: "1px solid #dbdfe4", borderRadius: 0 }}
                  >
                    <StyledButton
                      startIcon={
                        news.articleLink ? (
                          <WebRoundedIcon />
                        ) : (
                          <DoDisturbRoundedIcon />
                        )
                      }
                      onClick={() => handleButtonClick(news.articleLink)}
                      disabled={!news.articleLink}
                    >
                      {news.articleLink ? "Website" : "Unavailable"}
                    </StyledButton>
                    <StyledButton
                      startIcon={<DescriptionOutlinedIcon />}
                      onClick={() => handleButtonClick(news.pdfLink)}
                    >
                      PDF
                    </StyledButton>
                  </ButtonGroup>
                </StyledPaper>
              </Grid>
            ))
          ) : (
            <h2>No articles found</h2>
          )
        ) : (
          [...Array(MAX)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={`news-${index}`}>
              <StyledPaper elevation={0}>
                <Skeleton variant="rectangular" width={50} height={30} />
                <p>
                  <Skeleton variant="text" width={40} height={16} />
                </p>
                <h3>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="50%" height={20} />
                </h3>
              </StyledPaper>
            </Grid>
          ))
        )}
      </Grid>

      {!compact && (
        <PaginationRoot>
          <Pagination
            color="primary"
            size="large"
            count={Math.ceil((newsList?.length || 0) / MAX)}
            page={page}
            onChange={handleChange}
          />
        </PaginationRoot>
      )}
    </section>
  );
};

const StyledPaper = styled(Paper)(() => ({
  padding: "0",
  height: "100%",
  margin: "1rem 0",
  textAlign: "left",
  border: `1px solid #dbdfe4`,
  borderRadius: "1rem",
  transition: "all 0.2s ease-in-out",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  overflow: "hidden",

  "& .cardContent": {
    padding: "2rem 2rem 0",
  },

  "& .logoHolder": {
    width: "100px",
    height: "auto",
  },

  "& .cardDate": {
    color: "#4d586d",
  },

  "& h3": {
    marginBottom: "0",
    color: "#444",
    fontSize: "1rem",
    fontWeight: "700",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderColor: "#dbdfe4!important",
  padding: "15px 10px",
  textTransform: "capitalize",
  color: "#5a6377",
  fontWeight: "600",
  fontSize: "14px",

  "& span": {
    marginRight: "12px",
    color: "#9ca3af",
  },

  "&:hover": {
    color: "#2b354a",
    backgroundColor: theme.palette.primary.light,
  },

  "&:hover span": {
    color: "#2b354a",
  },

  "&:disabled, &:disabled span": {
    color: "#b5bfc8",
    fontWeight: "500!important",
  },
}));

const PaginationRoot = styled("div")(() => ({
  marginTop: "5rem",
  display: "flex",
  justifyContent: "center",
}));

interface Props {
  strings: any;
  newsList: Press[];
  loading: boolean;
  compact?: boolean;
}

export default InTheNews;
